import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Play, Pause, RotateCcw, ChevronLeft, ChevronRight, Minus, Plus, Music2, ZoomIn, ZoomOut, ExternalLink, FileText, Crown, Radio, WifiOff, Check, X, Users, GripVertical, ListOrdered, Type } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fileUrl } from "@/components/FileViewer";
import { transposeChordLine, transposeKey, preferFlatsFor, sanitizeChordText } from "@/lib/chords";
import { metaFor } from "@/lib/sections";

const NOTES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const FLAT = { Db: "C#", Eb: "D#", Gb: "F#", Ab: "G#", Bb: "A#" };
const CHORD_RE = /\b([A-G])(#|b)?(m|maj|min|dim|aug|sus|add)?\d*(\/[A-G](#|b)?)?\b/g;

function transposeChord(chord, steps) {
  return chord.replace(/([A-G])(#|b)?/g, (m, root, acc) => {
    let name = root + (acc || "");
    if (FLAT[name]) name = FLAT[name];
    let idx = NOTES.indexOf(name);
    if (idx < 0) return m;
    return NOTES[(idx + steps + 12) % 12];
  });
}

function transposeLine(line, steps) {
  if (steps === 0) return line;
  // heuristic: only transpose lines that look like chord lines (mostly chords/spaces)
  const tokens = line.trim().split(/\s+/).filter(Boolean);
  const chordish = tokens.length > 0 && tokens.every((t) => /^[A-G](#|b)?(m|maj|min|dim|aug|sus|add|\d|\/|[A-G]|#|b)*$/.test(t));
  if (!chordish) return line;
  return line.replace(/([A-G](#|b)?(m|maj|min|dim|aug|sus|add)?\d*(\/[A-G](#|b)?)?)/g, (m) => transposeChord(m, steps));
}

const STAGE_TONE = { REFRAO: "#FF4D00", PRE_REFRAO: "#FFB020", POS_REFRAO: "#FFB020", FINAL: "#34d399", OUTRO: "#34d399", PONTE: "#a78bfa", SOLO: "#a78bfa", MEDLEY: "#a78bfa", TRANSICAO: "#71717a", INTRO: "#00F0FF" };
function stageTone(t) { return STAGE_TONE[t] || "#00F0FF"; }

export default function StageMode() {
  const { id } = useParams();
  const { hasFeature } = useAuth();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [idx, setIdx] = useState(0);
  const [font, setFont] = useState(24);
  const [keysMap, setKeysMap] = useState({});
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(40);
  const [scale, setScale] = useState(1);
  const scrollRef = useRef(null);
  const [session, setSession] = useState(null);
  const [following, setFollowing] = useState(false);
  const lastScrollPush = useRef(0);
  const applyingRemote = useRef(false);
  const [disconnected, setDisconnected] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [showSetlist, setShowSetlist] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const isMaestro = !!session?.is_maestro;
  const isCreator = !!session?.is_creator;
  const lastPushed = useRef(null);
  const steps = keysMap[idx] || 0;
  const [secIdx, setSecIdx] = useState(0);
  const secRefs = useRef({});
  const navRelRef = useRef(() => {});
  const stageTouch = useRef({ x: 0, y: 0 });
  const onStageTouchStart = (e) => { const t = e.touches[0]; stageTouch.current = { x: t.clientX, y: t.clientY }; };
  const onStageTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - stageTouch.current.x, dy = t.clientY - stageTouch.current.y;
    if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 1.5) navRelRef.current(dx < 0 ? 1 : -1);
  };
  const [secSpeed, setSecSpeed] = useState({});

  // Modo Palco usa o tema "stage" (alto contraste); restaura o tema do usuário ao sair
  useEffect(() => {
    const prev = document.documentElement.getAttribute("data-theme");
    document.documentElement.setAttribute("data-theme", "stage");
    return () => { if (prev) document.documentElement.setAttribute("data-theme", prev); };
  }, []);

  // Navegação entre músicas por teclado (setas ← →)
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target.isContentEditable) return;
      if (e.key === "ArrowRight") { e.preventDefault(); navRelRef.current(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); navRelRef.current(-1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // tom sincronizado POR MÚSICA (item 1): seguidores/participantes espelham o tom do Maestro
  useEffect(() => {
    if (!isMaestro && session && session.keys && typeof session.keys === "object") {
      const m = {}; Object.entries(session.keys).forEach(([k, v]) => { m[parseInt(k, 10)] = parseInt(v, 10); });
      setKeysMap(m);
    }
  }, [session?.keys, session?.updated_at, isMaestro]);

  const changeTom = (delta) => {
    if (following) return; // seguidores acompanham o tom do Maestro
    setKeysMap((prev) => {
      const nm = { ...prev, [idx]: (prev[idx] || 0) + delta };
      if (isMaestro) api.put(`/events/${id}/stage-session`, { keys: nm }).catch(() => {});
      return nm;
    });
  };

  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/events/${id}/stage-session`);
        if (stop) return;
        setSession(data); setDisconnected(false);
      } catch { if (!stop) setDisconnected(true); }
    };
    tick();
    const t = setInterval(tick, 1500);
    return () => { stop = true; clearInterval(t); };
  }, [id]);

  useEffect(() => {
    if (session?.is_creator) {
      api.get(`/events/${id}/applicants`).then(({ data }) => setParticipants(data.filter((a) => ["aprovado", "confirmado", "concluido"].includes(a.status)))).catch(() => {});
    }
  }, [session?.is_creator, id]);

  // follower: mirror maestro's current index in real time
  useEffect(() => {
    if (following && session && typeof session.index === "number") setIdx(session.index);
  }, [following, session?.index, session?.updated_at]);

  // follower: mirror maestro's current SECTION (pula todos juntos para o Refrão etc.)
  useEffect(() => {
    if (!following || !session || typeof session.section !== "number") return;
    setSecIdx(session.section);
    const el = secRefs.current[session.section];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [following, session?.section, session?.updated_at]);

  // follower: mirror maestro's scroll position (item 3) — evita loop com applyingRemote
  useEffect(() => {
    if (!following || !session || typeof session.scroll !== "number") return;
    const el = scrollRef.current; if (!el) return;
    applyingRemote.current = true;
    el.scrollTop = session.scroll * Math.max(0, el.scrollHeight - el.clientHeight);
    const t = setTimeout(() => { applyingRemote.current = false; }, 150);
    return () => clearTimeout(t);
  }, [following, session?.scroll, session?.updated_at, idx]);

  // maestro: broadcast scroll position (auto + manual), throttled
  const updateVisibleSection = () => {
    const el = scrollRef.current; if (!el) return;
    const contTop = el.getBoundingClientRect().top;
    let cur = 0;
    for (const k of Object.keys(secRefs.current)) {
      const s = secRefs.current[k]; if (!s) continue;
      if (s.getBoundingClientRect().top - contTop <= 12) cur = Math.max(cur, parseInt(k, 10));
    }
    setSecIdx((p) => (p === cur ? p : cur));
  };
  const onLyricsScroll = () => {
    updateVisibleSection();
    if (!isMaestro || applyingRemote.current) return;
    const el = scrollRef.current; if (!el) return;
    const now = Date.now();
    if (now - lastScrollPush.current < 200) return;
    lastScrollPush.current = now;
    const frac = el.scrollHeight > el.clientHeight ? el.scrollTop / (el.scrollHeight - el.clientHeight) : 0;
    api.put(`/events/${id}/stage-session`, { scroll: frac }).catch(() => {});
  };

  // maestro: broadcast current index
  useEffect(() => {
    if (!isMaestro) return;
    if (lastPushed.current === idx) return;
    lastPushed.current = idx;
    api.put(`/events/${id}/stage-session`, { index: idx }).catch(() => {});
  }, [idx, isMaestro, id]);

  // follower: send presence heartbeat so the maestro sees a live follower count
  useEffect(() => {
    if (!session?.has_maestro || isMaestro) return;
    api.post(`/events/${id}/stage-heartbeat`, { following }).catch(() => {});
    if (!following) return;
    const t = setInterval(() => api.post(`/events/${id}/stage-heartbeat`, { following: true }).catch(() => {}), 2500);
    return () => clearInterval(t);
  }, [following, session?.has_maestro, isMaestro, id]);

  // maestro: drag-and-drop reorder of the live setlist (reflects to followers)
  const moveSong = async (from, to) => {
    if (from === to || from == null || to == null) return;
    const rep = [...(ev.repertoire || [])];
    const order = rep.map((_, i) => i);
    const [oi] = order.splice(from, 1); order.splice(to, 0, oi);
    const newRep = order.map((i) => rep[i]);
    const current = rep[Math.min(idx, rep.length - 1)];
    setEv({ ...ev, repertoire: newRep });
    const newIdx = newRep.indexOf(current);
    if (newIdx >= 0) { setIdx(newIdx); lastPushed.current = newIdx; }
    try { await api.put(`/events/${id}/stage-session`, { order, index: newIdx >= 0 ? newIdx : idx }); }
    catch { toast.error("Não foi possível reordenar."); }
  };

  const refreshSession = async () => { try { const { data } = await api.get(`/events/${id}/stage-session`); setSession(data); } catch {} };
  const assignMaestro = async (uid) => {
    try {
      if (uid) { await api.post(`/events/${id}/maestro`, { user_id: uid }); toast.success("Maestro definido!"); }
      else { await api.delete(`/events/${id}/maestro`); toast.success("Maestro removido."); }
      refreshSession();
    } catch (e) { toast.error(e.response?.data?.detail || "Não foi possível definir o Maestro."); }
  };
  const approveMaestro = async (uid, ok) => { try { await api.post(`/events/${id}/maestro/approve`, { user_id: uid, approve: ok }); toast.success(ok ? "Maestro aprovado!" : "Pedido recusado."); refreshSession(); } catch (e) { toast.error(e.response?.data?.detail || "Ação falhou."); } };
  const requestMaestro = async () => { try { await api.post(`/events/${id}/maestro/request`); toast.success("Pedido enviado ao criador."); refreshSession(); } catch (e) { toast.error(e.response?.data?.detail || "Não foi possível solicitar."); } };

  useEffect(() => { api.get(`/events/${id}`).then((r) => setEv(r.data)).catch(() => toast.error("Evento não encontrado")); }, [id]);

  useEffect(() => {
    if (!playing || following) return;
    const el = scrollRef.current;
    if (!el) return;
    const spd = (secSpeed[secIdx] != null) ? secSpeed[secIdx] : speed;
    const t = setInterval(() => { el.scrollTop += 1; }, Math.max(10, 110 - spd));
    return () => clearInterval(t);
  }, [playing, speed, secSpeed, secIdx, idx, following]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = 0; setPlaying(false); setScale(1); setSecIdx(0); }, [idx]);

  const jumpTo = (i) => {
    setIdx(i); lastPushed.current = i;
    if (isMaestro) api.put(`/events/${id}/stage-session`, { index: i }).catch(() => {});
    if (following) setFollowing(false);
    setShowSetlist(false);
  };

  if (!ev) return <div className="text-zinc-500">Carregando...</div>;

  const songs = (following && session?.repertoire?.length ? session.repertoire : (ev.repertoire || []));
  navRelRef.current = (delta) => {
    const ni = Math.min(songs.length - 1, Math.max(0, idx + delta));
    if (ni === idx) return;
    jumpTo(ni);
  };
  if (ev.repertoire_locked || songs.length === 0) {
    return (
      <div className="max-w-lg mx-auto text-center py-20">
        <Music2 className="h-12 w-12 mx-auto text-zinc-600 mb-4" />
        <h1 className="font-head text-2xl font-bold">Repertório indisponível</h1>
        <p className="text-zinc-500 mt-2">{songs.length === 0 ? "Este evento não possui repertório cadastrado." : "O repertório completo será liberado após a confirmação da sua participação."}</p>
        <Button onClick={() => navigate(`/eventos/${id}`)} className="mt-6 bg-[#FF4D00] hover:bg-[#E64500] rounded-sm">Voltar ao evento</Button>
      </div>
    );
  }

  const song = songs[Math.min(idx, songs.length - 1)] || songs[0];
  const displayKey = song.key ? transposeKey(song.key, steps, preferFlatsFor(song.key)) : "";
  const isFile = !!song.file_id;
  const structured = !isFile && song.structured && Array.isArray(song.structured.sections) && song.structured.sections.length ? song.structured : null;
  const pf = preferFlatsFor(song.key || "");
  const jumpToSection = (i) => {
    setSecIdx(i);
    const el = secRefs.current[i];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    if (isMaestro) api.put(`/events/${id}/stage-session`, { section: i }).catch(() => {});
    else if (following) setFollowing(false);
  };

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black flex flex-col stage-screen" data-testid="stage-mode">
      {/* barra única — cabeçalho + controles */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/15 bg-[#0a0a0a]" data-testid="stage-controls">
        <button data-testid="stage-exit" onClick={() => navigate(`/eventos/${id}`)} aria-label="Sair do Modo Palco" className="flex items-center gap-1 text-sm text-zinc-300 hover:text-white shrink-0 min-h-[40px]"><ArrowLeft className="h-5 w-5" /><span className="hidden md:inline">Sair</span></button>
        <div className="min-w-0 flex-1 text-center">
          <div className="font-head font-bold text-sm sm:text-lg truncate leading-tight text-white">{song.title}{song.version_label && <span className="text-zinc-400 font-normal"> · {song.version_label}</span>}{songs.length > 1 && <span className="text-[10px] font-mono text-zinc-500 ml-2" data-testid="stage-page-count">{Math.min(idx, songs.length - 1) + 1}/{songs.length}</span>}</div>
        </div>
        <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
        {isFile ? (
          song.file_kind === "image" ? (
            <>
              <button data-testid="stage-zoom-out" aria-label="Diminuir zoom" onClick={() => setScale((s) => Math.max(0.5, s - 0.25))} className="h-10 w-10 grid place-items-center rounded-lg text-zinc-200 hover:bg-white/10 transition-colors"><ZoomOut className="h-5 w-5" /></button>
              <button data-testid="stage-zoom-in" aria-label="Aumentar zoom" onClick={() => setScale((s) => Math.min(4, s + 0.25))} className="h-10 w-10 grid place-items-center rounded-lg text-zinc-200 hover:bg-white/10 transition-colors"><ZoomIn className="h-5 w-5" /></button>
              <button aria-label="Redefinir zoom" onClick={() => setScale(1)} className="h-10 w-10 grid place-items-center rounded-lg text-zinc-200 hover:bg-white/10 transition-colors"><RotateCcw className="h-5 w-5" /></button>
            </>
          ) : (
            <span className="text-xs text-zinc-500 px-2">Arquivo original · {({ pdf: "PDF", word: "Word" }[song.file_kind] || "Arquivo")}</span>
          )
        ) : (
          <>
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="stage-scroll-trigger" aria-label="Rolagem automática" className={`h-10 w-10 grid place-items-center rounded-lg hover:bg-white/10 data-[state=open]:bg-white/10 transition-colors ${playing ? "text-[#FF4D00]" : "text-zinc-100"}`}>{playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}</button>
              </PopoverTrigger>
              <PopoverContent align="center" sideOffset={8} className="w-64 p-3 z-[80] bg-[#111114] border-white/15 text-white rounded-xl">
                <button data-testid="stage-play" onClick={() => setPlaying((p) => !p)} className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#FF4D00] hover:bg-[#E64500] text-white py-2 font-semibold mb-3">{playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}{playing ? "Pausar rolagem" : "Rolar automaticamente"}</button>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zinc-400 uppercase tracking-wide w-16">Velocidade</span>
                  <input type="range" min="10" max="100" value={speed} onChange={(e) => setSpeed(parseInt(e.target.value))} className="flex-1 h-2 accent-[#FF4D00]" aria-label="Velocidade da rolagem" />
                </div>
                {structured && (
                  <div className="flex items-center gap-2 mt-2" data-testid="stage-section-speed">
                    <span className="text-[11px] text-[#00F0FF] uppercase tracking-wide w-16 leading-tight">Vel. seção</span>
                    <input type="range" min="10" max="100" value={secSpeed[secIdx] ?? speed} onChange={(e) => setSecSpeed((m) => ({ ...m, [secIdx]: parseInt(e.target.value) }))} className="flex-1 h-2 accent-[#00F0FF]" aria-label="Velocidade da rolagem desta seção" />
                    {secSpeed[secIdx] != null && <button data-testid="stage-section-speed-reset" onClick={() => setSecSpeed((m) => { const n = { ...m }; delete n[secIdx]; return n; })} className="text-[10px] text-zinc-400 underline shrink-0">padrão</button>}
                  </div>
                )}
                <button onClick={() => { if (scrollRef.current) scrollRef.current.scrollTop = 0; }} className="w-full mt-3 flex items-center justify-center gap-1 rounded-lg border border-white/20 py-2 text-sm text-zinc-200 hover:bg-white/10"><RotateCcw className="h-4 w-4" />Voltar ao topo</button>
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="stage-tom-trigger" aria-label="Tom" className={`h-10 min-w-[42px] px-1.5 grid place-items-center rounded-lg hover:bg-white/10 data-[state=open]:bg-white/10 transition-colors font-mono font-bold text-lg leading-none ${displayKey ? "text-[#00F0FF]" : "text-zinc-400"}`}>{displayKey || "♪"}</button>
              </PopoverTrigger>
              <PopoverContent align="center" sideOffset={8} className="w-auto p-3 z-[80] bg-[#111114] border-white/15 text-white rounded-xl" data-testid="stage-tom-control">
                <div className="text-[10px] uppercase tracking-wide text-zinc-400 text-center mb-2">Tom {following ? "(segue o Maestro)" : ""}</div>
                <div className="flex items-center justify-center gap-3">
                  <button data-testid="stage-transpose-down" disabled={following} onClick={() => changeTom(-1)} aria-label="Baixar meio tom" className="h-11 w-11 grid place-items-center rounded-lg border border-white/20 text-white hover:bg-white/10 disabled:opacity-30"><Minus className="h-5 w-5" /></button>
                  <span className="font-mono text-2xl w-12 text-center text-[#00F0FF]">{steps > 0 ? `+${steps}` : steps}</span>
                  <button data-testid="stage-transpose-up" disabled={following} onClick={() => changeTom(1)} aria-label="Subir meio tom" className="h-11 w-11 grid place-items-center rounded-lg border border-white/20 text-white hover:bg-white/10 disabled:opacity-30"><Plus className="h-5 w-5" /></button>
                </div>
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="stage-font-trigger" aria-label="Tamanho da fonte" className="h-10 w-10 grid place-items-center rounded-lg text-zinc-100 hover:bg-white/10 data-[state=open]:bg-white/10 transition-colors"><Type className="h-5 w-5" /></button>
              </PopoverTrigger>
              <PopoverContent align="center" sideOffset={8} className="w-auto p-3 z-[80] bg-[#111114] border-white/15 text-white rounded-xl" data-testid="stage-font">
                <div className="text-[10px] uppercase tracking-wide text-zinc-400 text-center mb-2">Fonte</div>
                <div className="flex items-center justify-center gap-3">
                  <button data-testid="stage-font-down" onClick={() => setFont((f) => Math.max(16, f - 2))} aria-label="Diminuir fonte" className="h-11 w-11 grid place-items-center rounded-lg border border-white/20 text-white hover:bg-white/10"><Minus className="h-5 w-5" /></button>
                  <span className="font-mono text-xl w-10 text-center text-white">{font}</span>
                  <button data-testid="stage-font-up" onClick={() => setFont((f) => Math.min(56, f + 2))} aria-label="Aumentar fonte" className="h-11 w-11 grid place-items-center rounded-lg border border-white/20 text-white hover:bg-white/10"><Plus className="h-5 w-5" /></button>
                </div>
              </PopoverContent>
            </Popover>
          </>
        )}
        </div>
      </div>

      {songs.length > 1 && (
        <>
          <button data-testid="stage-nav-prev" disabled={idx <= 0} onClick={() => jumpTo(Math.max(0, idx - 1))} aria-label="Música anterior" className="absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 z-30 h-11 w-11 grid place-items-center rounded-full bg-black/40 backdrop-blur-sm text-white/50 hover:text-white hover:bg-black/70 border border-white/10 disabled:opacity-0 disabled:pointer-events-none transition-colors"><ChevronLeft className="h-6 w-6" /></button>
          <button data-testid="stage-nav-next" disabled={idx >= songs.length - 1} onClick={() => jumpTo(Math.min(songs.length - 1, idx + 1))} aria-label="Próxima música" className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 z-30 h-11 w-11 grid place-items-center rounded-full bg-black/40 backdrop-blur-sm text-white/50 hover:text-white hover:bg-black/70 border border-white/10 disabled:opacity-0 disabled:pointer-events-none transition-colors"><ChevronRight className="h-6 w-6" /></button>
        </>
      )}

      {/* Maestro bar */}
      <div className="flex items-center flex-wrap gap-2 px-4 py-2 border-b border-white/10 bg-[#0d0d0f]" data-testid="maestro-bar">
        {disconnected && <span data-testid="maestro-reconnecting" className="text-xs text-amber-400 flex items-center gap-1"><WifiOff className="h-3.5 w-3.5" />Reconectando ao Maestro...</span>}
        {isMaestro ? (
          <>
            <span data-testid="maestro-active" className="text-xs px-2 py-1 rounded-sm bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 flex items-center gap-1"><Crown className="h-3.5 w-3.5" />Modo Maestro Ativo — suas ações guiam o time</span>
            <span data-testid="follower-count" className="text-xs px-2 py-1 rounded-sm bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 flex items-center gap-1"><Users className="h-3.5 w-3.5" />{session?.follower_count || 0} seguindo</span>
            <button data-testid="open-setlist-btn" onClick={() => setShowSetlist(true)} className="text-xs px-3 py-1 rounded-sm border border-white/15 text-zinc-200 flex items-center gap-1 hover:bg-white/5"><ListOrdered className="h-3.5 w-3.5" />Reordenar setlist</button>
          </>
        ) : session?.has_maestro ? (
          <button data-testid="follow-maestro-toggle" onClick={() => setFollowing((f) => !f)} className={`text-xs px-3 py-1 rounded-sm border flex items-center gap-1 ${following ? "bg-[#00F0FF]/15 text-[#00F0FF] border-[#00F0FF]/40" : "bg-transparent text-zinc-300 border-white/15"}`}>
            <Radio className="h-3.5 w-3.5" />Seguir Maestro: {following ? "ON" : "OFF"} <span className="text-zinc-500">({session.maestro_name})</span>
          </button>
        ) : session?.can_be_maestro && hasFeature("maestro_sync") ? (
          <button data-testid="request-maestro-btn" disabled={session.already_requested} onClick={requestMaestro} className="text-xs px-3 py-1 rounded-sm border border-[#FF4D00]/40 text-[#FF4D00] disabled:opacity-40 flex items-center gap-1"><Crown className="h-3.5 w-3.5" />{session.already_requested ? "Solicitação enviada" : "Solicitar ser Maestro"}</button>
        ) : null}
        {isCreator && (
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <select data-testid="assign-maestro-select" value={session?.maestro_id || ""} onChange={(e) => assignMaestro(e.target.value)} className="text-xs bg-[#09090B] border border-white/15 rounded-sm px-2 py-1 text-zinc-200">
              <option value="">Definir Maestro…</option>
              {participants.map((p) => <option key={p.user_id} value={p.user_id}>{p.stage_name}</option>)}
            </select>
            {(session?.requests || []).map((r) => (
              <span key={r.user_id} data-testid={`maestro-request-${r.user_id}`} className="text-xs flex items-center gap-1 bg-[#1C1C24] rounded-sm px-2 py-1">
                {r.stage_name}
                <button data-testid={`approve-maestro-${r.user_id}`} onClick={() => approveMaestro(r.user_id, true)} className="text-emerald-400"><Check className="h-3.5 w-3.5" /></button>
                <button data-testid={`reject-maestro-${r.user_id}`} onClick={() => approveMaestro(r.user_id, false)} className="text-red-400"><X className="h-3.5 w-3.5" /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* content: original file OR lyrics */}
      {isFile ? (
        <div className="flex-1 overflow-hidden bg-black flex items-center justify-center" data-testid="stage-file">
          {song.file_kind === "image" && (
            <div className="h-full w-full overflow-auto flex items-center justify-center p-2">
              <img data-testid="stage-image" src={fileUrl(song.file_id)} alt={song.title} draggable={false}
                style={{ transform: `scale(${scale})`, transition: "transform 0.15s" }} className="max-h-full max-w-full object-contain select-none" />
            </div>
          )}
          {song.file_kind === "pdf" && (
            <iframe data-testid="stage-pdf" title={song.title} src={fileUrl(song.file_id)} className="w-full h-full bg-white" />
          )}
          {song.file_kind === "word" && (
            <div className="text-center space-y-4">
              <FileText className="h-14 w-14 text-[#00F0FF] mx-auto" />
              <p className="text-zinc-400 text-sm px-6">Documentos Word não abrem em tela. Toque para abrir em nova aba.</p>
              <a href={fileUrl(song.file_id)} target="_blank" rel="noreferrer"><Button className="bg-[#FF4D00] hover:bg-[#E64500] rounded-sm gap-2" data-testid="stage-word-open"><ExternalLink className="h-4 w-4" />Abrir documento</Button></a>
            </div>
          )}
        </div>
      ) : structured ? (
        <>
          <div className="flex items-center gap-1 px-2 py-2 border-b border-white/10 bg-[#0d0d0f]" data-testid="stage-section-nav">
            <button data-testid="stage-section-prev" aria-label="Seção anterior" disabled={secIdx <= 0} onClick={() => jumpToSection(Math.max(0, secIdx - 1))} className="shrink-0 h-9 w-9 rounded-md border border-white/15 text-zinc-200 disabled:opacity-30 flex items-center justify-center hover:bg-white/5"><ChevronLeft className="h-5 w-5" /></button>
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar flex-1 px-1">
              {structured.sections.map((sec, i) => {
                const meta = metaFor(sec.type); const active = i === secIdx;
                const num = (sec.title.match(/\d+/) || [""])[0];
                return (
                  <button key={sec.id || i} data-testid={`stage-nav-${i}`} onClick={() => jumpToSection(i)}
                    className={`shrink-0 text-xs font-bold uppercase tracking-wide px-2.5 py-1.5 rounded-md border transition-colors ${active ? "bg-[#FF4D00] text-white border-[#FF4D00]" : "bg-transparent text-zinc-300 border-white/15 hover:border-white/40"}`}>
                    {meta.short}{num && meta.short === "V" ? num : ""}
                  </button>
                );
              })}
            </div>
            <button data-testid="stage-section-next" aria-label="Próxima seção" disabled={secIdx >= structured.sections.length - 1} onClick={() => jumpToSection(Math.min(structured.sections.length - 1, secIdx + 1))} className={`shrink-0 h-9 px-2 rounded-md border flex items-center justify-center gap-1 disabled:opacity-30 ${isMaestro ? "bg-[#FF4D00] text-white border-[#FF4D00]" : "border-white/15 text-zinc-200 hover:bg-white/5"}`}>{isMaestro && <span className="text-[10px] font-bold uppercase hidden sm:inline">Avançar</span>}<ChevronRight className="h-5 w-5" /></button>
          </div>
          <div ref={scrollRef} onScroll={onLyricsScroll} onTouchStart={onStageTouchStart} onTouchEnd={onStageTouchEnd} data-testid="stage-lyrics" className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 space-y-5">
            {structured.sections.map((sec, i) => {
              const secTom = sec.key ? transposeKey(sec.key, steps, pf) : "";
              return (
                <section key={sec.id || i} ref={(el) => (secRefs.current[i] = el)} data-testid={`stage-section-${i}`} className="scroll-mt-2">
                  {sec.song_title && (
                    <div className="flex items-center gap-2 text-[#00F0FF] font-bold text-sm uppercase tracking-wide mb-1 border-b border-white/10 pb-1" data-testid={`stage-subsong-${i}`}>
                      <Music2 className="h-4 w-4" /><span className="truncate">{sec.song_title}{sec.song_artist ? ` · ${sec.song_artist}` : ""}</span>
                      {secTom && <span className="ml-auto text-zinc-400 font-mono">Tom {secTom}</span>}
                    </div>
                  )}
                  <div className="font-head font-black uppercase tracking-wide mb-1 flex items-center gap-2 flex-wrap" style={{ color: stageTone(sec.type), fontSize: `${Math.max(15, Math.round(font * 0.7))}px` }}>
                    {sec.title}{sec.repeat > 1 ? <span className="text-zinc-400 font-mono text-base">×{sec.repeat}</span> : null}
                    {secTom && !sec.song_title ? <span className="text-zinc-400 font-mono text-sm">Tom {secTom}</span> : null}
                  </div>
                  <div className="space-y-0.5">
                    {sec.lines.map((l, j) => {
                      if (l.kind === "annotation") return <div key={j} className="text-amber-400 font-bold uppercase text-sm py-0.5">{sanitizeChordText(l.text)}</div>;
                      const rawc = sanitizeChordText(l.chords);
                      const ly = sanitizeChordText(l.lyrics);
                      const chords = steps ? transposeChordLine(rawc, steps, pf) : rawc;
                      return (
                        <div key={j} className="leading-tight">
                          {chords ? <div className="font-mono font-bold text-[#FF6A2B] whitespace-pre-wrap break-words" style={{ fontSize: `${font}px` }}>{chords}</div> : null}
                          {ly ? <div className="font-mono text-zinc-100 whitespace-pre-wrap break-words" style={{ fontSize: `${font}px` }}>{ly}</div> : null}
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        </>
      ) : (
        <div ref={scrollRef} onScroll={onLyricsScroll} onTouchStart={onStageTouchStart} onTouchEnd={onStageTouchEnd} data-testid="stage-lyrics" className="flex-1 overflow-y-auto px-6 py-8">
          <pre className="whitespace-pre-wrap font-mono text-zinc-100 leading-relaxed" style={{ fontSize: `${font}px` }}>
            {(song.lyrics || "Sem cifra/letra cadastrada para esta música.").split("\n").map((l, i) => {
              const t = transposeLine(l, steps);
              const isChord = (t !== l) || (l.trim().length > 0 && /[A-G]/.test(l) && /^[\sA-G#bmajinsdugMAJ0-9\/()+-]+$/.test(l.trim()));
              return <div key={`${i}:${l.slice(0, 16)}`} className={isChord ? "text-[#FF4D00] font-semibold" : ""}>{t || "\u00A0"}</div>;
            })}
          </pre>
        </div>
      )}

      {/* setlist reorder panel (maestro only) */}
      {showSetlist && (
        <div className="absolute inset-0 z-[60] bg-black/70 flex justify-end" data-testid="setlist-panel" onClick={() => setShowSetlist(false)}>
          <div className="w-full max-w-sm h-full bg-[#0d0d0f] border-l border-white/10 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="flex items-center gap-2 font-head font-bold"><ListOrdered className="h-4 w-4 text-[#00F0FF]" />{isMaestro ? "Reordenar setlist" : "Lista de músicas"}</div>
              <button onClick={() => setShowSetlist(false)} data-testid="close-setlist-btn" className="text-zinc-400 hover:text-white p-2 -mr-2"><X className="h-5 w-5" /></button>
            </div>
            <p className="px-4 py-2 text-xs text-zinc-500">{isMaestro ? "Arraste para reordenar. Toque para ir à música. A ordem é aplicada ao vivo." : "Toque em uma música para abri-la."}</p>
            <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-1.5">
              {(ev.repertoire || []).map((s, i) => (
                <div key={`${i}:${s.title}`} draggable={isMaestro} data-testid={`setlist-item-${i}`}
                  onClick={() => jumpTo(i)}
                  onDragStart={() => setDragIdx(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { moveSong(dragIdx, i); setDragIdx(null); }}
                  onDragEnd={() => setDragIdx(null)}
                  className={`flex items-center gap-2 rounded-sm border px-3 py-3 cursor-pointer ${isMaestro ? "active:cursor-grabbing" : ""} ${i === Math.min(idx, (ev.repertoire || []).length - 1) ? "border-[#FF4D00]/50 bg-[#FF4D00]/10" : "border-white/10 bg-[#121217]"} ${dragIdx === i ? "opacity-40" : ""}`}>
                  {isMaestro && <GripVertical className="h-4 w-4 text-zinc-500 shrink-0" />}
                  <span className="text-xs text-zinc-500 w-5 shrink-0 mono">{i + 1}</span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{s.title}{s.version_label && <span className="text-zinc-500 font-normal"> · {s.version_label}</span>}</div>
                    <div className="text-xs text-zinc-500 truncate">{s.artist}{s.key && ` · Tom ${s.key}`}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
